<template>
  <section class="tool-page page-shell community-page">
    <div class="section-heading compact-heading">
      <button class="back-link" @click="navigate('home')">← Home</button>
      <span class="eyebrow"><span></span>A little help from a stranger</span>
      <h1>Sometimes you just need <em>a gentle nudge.</em></h1>
      <p>Ask one clear question. Offer one useful thought. Keep it kind.</p>
    </div>

    <div class="segmented community-tabs">
      <button :class="{ active: tab === 'polls' }" @click="tab = 'polls'">◎ 15-minute polls</button>
      <button :class="{ active: tab === 'notes' }" @click="tab = 'notes'">⌁ Passing notes</button>
    </div>

    <div v-if="tab === 'polls'" class="community-content">
      <div class="community-toolbar"><div><strong>Help someone choose</strong><small>Vote with your first instinct.</small></div><button class="primary-button" @click="showCreatePoll = true">＋ Ask the crowd</button></div>
      <div class="poll-grid">
        <article v-for="poll in polls" :key="poll.id" class="poll-card">
          <div class="poll-meta"><span :class="{ ended: poll.expired }">{{ poll.expired ? 'RESULT' : timeLeft(poll.expires_at) }}</span><small>{{ poll.total_votes }} votes</small></div>
          <h3>{{ poll.question }}</h3>
          <div class="poll-options">
            <button v-for="choice in ['a', 'b']" :key="choice" :disabled="poll.voted || poll.expired" @click="vote(poll, choice)">
              <img v-if="poll[`option_${choice}_image`]" :src="poll[`option_${choice}_image`]" :alt="poll[`option_${choice}`]">
              <span class="poll-letter">{{ choice.toUpperCase() }}</span><strong>{{ poll[`option_${choice}`] }}</strong>
              <i v-if="poll.voted || poll.expired" :style="{ width: `${percentage(poll, choice)}%` }"></i>
              <em v-if="poll.voted || poll.expired">{{ percentage(poll, choice) }}%</em>
            </button>
          </div>
          <p v-if="poll.voted" class="voted-note">Thanks. Your first instinct counted.</p>
        </article>
      </div>
      <div v-if="!polls.length" class="empty-state"><span>◎</span><h3>A quiet room, for now.</h3><p>Be the first to ask for a nudge.</p></div>
    </div>

    <div v-else class="community-content notes-layout">
      <div class="note-compose">
        <span class="step-label">LEAVE ONE THOUGHT</span><h2>Say the useful thing.</h2><p>One sentence of encouragement or one concrete suggestion. No debates.</p>
        <textarea v-model.trim="newNote" maxlength="240" rows="4" placeholder="Something kind and specific…"></textarea>
        <div><small>{{ newNote.length }}/240</small><button class="primary-button" :disabled="newNote.length < 4" @click="postNote">Post anonymously</button></div>
      </div>
      <div class="note-wall">
        <article v-for="note in notes" :key="note.id"><span>“</span><p>{{ note.message }}</p><small>A passerby · {{ relativeDate(note.created_at) }}</small></article>
      </div>
    </div>

    <div v-if="showCreatePoll" class="result-overlay" @click.self="showCreatePoll = false">
      <div class="create-poll-dialog">
        <button class="dialog-close" @click="showCreatePoll = false">×</button>
        <span class="step-label">ASK THE CROWD</span><h2>Keep it to two.</h2><p>The poll stays open for 15 minutes.</p>
        <label>YOUR QUESTION<input v-model.trim="newPoll.question" maxlength="120" placeholder="Which one should I choose?"></label>
        <div class="new-poll-options">
          <label v-for="choice in ['a', 'b']" :key="choice"><span>OPTION {{ choice.toUpperCase() }}</span><input v-model.trim="newPoll[`option_${choice}`]" maxlength="80" :placeholder="choice === 'a' ? 'The first choice' : 'The second choice'">
            <button class="image-upload" type="button" @click="$refs[`file${choice}`][0].click()">{{ newPoll[`option_${choice}_image`] ? '✓ Image added' : '＋ Add an image' }}</button>
            <input :ref="`file${choice}`" class="hidden-file" type="file" accept="image/jpeg,image/png,image/webp" @change="readImage($event, choice)">
          </label>
        </div>
        <span v-if="pollError" class="form-error">{{ pollError }}</span>
        <button class="primary-button full-button" :disabled="creatingPoll" @click="createPoll">{{ creatingPoll ? 'Opening poll…' : 'Start 15-minute poll' }}</button>
      </div>
    </div>
  </section>
</template>

<script>
import { api } from '../services/api'

const demoPolls = [
  { id: 'demo-1', question: 'Which cover feels calmer for my journal?', option_a: 'Warm sand', option_b: 'Forest green', option_a_votes: 18, option_b_votes: 31, total_votes: 49, expires_at: new Date(Date.now() + 620000).toISOString(), voted: false },
  { id: 'demo-2', question: 'Friday night: go out or stay in?', option_a: 'Try the new place', option_b: 'Quiet night at home', option_a_votes: 24, option_b_votes: 20, total_votes: 44, expires_at: new Date(Date.now() + 310000).toISOString(), voted: false }
]
const demoNotes = [
  { id: 'n1', message: 'Choose the option that makes tomorrow morning feel lighter.', created_at: new Date(Date.now() - 1800000).toISOString() },
  { id: 'n2', message: 'You do not need certainty to take the next small step.', created_at: new Date(Date.now() - 7200000).toISOString() },
  { id: 'n3', message: 'Convenience is a valid reason. Not every choice needs a bigger story.', created_at: new Date(Date.now() - 86400000).toISOString() }
]

export default {
  name: 'CommunityView',
  inject: ['navigate', 'notify'],
  data: () => ({ tab: 'polls', polls: [], notes: [], showCreatePoll: false, creatingPoll: false, pollError: '', newNote: '', newPoll: { question: '', option_a: '', option_b: '', option_a_image: '', option_b_image: '' } }),
  created () { this.load() },
  methods: {
    async load () {
      try { const [pollData, noteData] = await Promise.all([api.polls(), api.notes()]); this.polls = pollData.polls; this.notes = noteData.notes } catch (_) { this.polls = demoPolls; this.notes = demoNotes }
    },
    percentage (poll, choice) { return poll.total_votes ? Math.round(Number(poll[`option_${choice}_votes`]) / Number(poll.total_votes) * 100) : 0 },
    parseDate (date) { return new Date(String(date).replace(' ', 'T')) },
    timeLeft (date) { const min = Math.max(0, Math.ceil((this.parseDate(date) - Date.now()) / 60000)); return `${min} MIN LEFT` },
    relativeDate (date) { const min = Math.floor((Date.now() - this.parseDate(date)) / 60000); return min < 60 ? `${Math.max(1, min)}m ago` : min < 1440 ? `${Math.floor(min / 60)}h ago` : `${Math.floor(min / 1440)}d ago` },
    async vote (poll, choice) {
      if (String(poll.id).startsWith('demo')) { poll[`option_${choice}_votes`]++; poll.total_votes++; this.$set(poll, 'voted', true); return }
      try { const data = await api.vote(poll.id, choice); Object.assign(poll, data.poll, { voted: true }) } catch (error) { this.notify(error.message) }
    },
    readImage (event, choice) {
      const file = event.target.files[0]
      if (!file) return
      if (file.size > 2 * 1024 * 1024) { this.pollError = 'Please use an image smaller than 2 MB.'; return }
      const reader = new FileReader(); reader.onload = e => { this.newPoll[`option_${choice}_image`] = e.target.result }; reader.readAsDataURL(file)
    },
    async createPoll () {
      if (!this.newPoll.question || !this.newPoll.option_a || !this.newPoll.option_b) { this.pollError = 'Add a question and both options.'; return }
      this.creatingPoll = true; this.pollError = ''
      try { const data = await api.createPoll(this.newPoll); this.polls.unshift(data.poll); this.showCreatePoll = false; this.newPoll = { question: '', option_a: '', option_b: '', option_a_image: '', option_b_image: '' } } catch (error) { this.pollError = error.message } finally { this.creatingPoll = false }
    },
    async postNote () {
      try { const data = await api.createNote({ message: this.newNote }); this.notes.unshift(data.note); this.newNote = '' } catch (_) { this.notes.unshift({ id: Date.now(), message: this.newNote, created_at: new Date().toISOString() }); this.newNote = '' }
    }
  }
}
</script>
